import Configuration from '@/agilis/util/Configuration'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import dialogFormMixin from '@/agilis/components/DialogForm/dialogFormMixin'
import L from 'lodash'
import UnStyledLink from '@/agilis/components/UnstyledLink/UnstyledLink.vue'
import DataTableWithGroups from '@/agilis/components/DataTableWithGroups/DataTableWithGroups.vue'

export default {
  name: 'DataTableV2ItemNestedTable',
  mixins: [deleteMixin, dialogFormMixin],

  components: {
    UnStyledLink,
    DataTableWithGroups
  },

  props: {
    trackerDTO: Object,
    entity: Object,
    entityType: String
  },

  data () {
    return {
      itemsKey: 'id',
      dialog: false,
      items: [],
      nestedTrackerDTO: {}
    }
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId'
    }),

    title () {
      return this.entity.name ? this.$t(this.entity.name) : this.$t('label.entity')
    },

    entityDatatable () {
      return this.entity.dataTable
    },

    entityTypeId () {
      return this.entity.entityTypeId || this.entityType
    },

    headers () {
      const headers = Configuration.auth.getHeaders(this.entityDatatable, this.selectedContext)

      return [
        ...headers,
        { value: 'actions', text: 'Actions' }
      ].filter(Boolean)
    },

    showExpand () {
      return this.entityDatatable.expandRows || false
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    fetchData () {
      this.entityDatatable.querySelectMode ? this.fetchNestedEntityQuery() : this.fetchNestedEntity()
    },

    fetchNestedEntity () {
      this.startLoader({ id: 'tracker-nested' })
      const url = Vue.axios.agilisUrlNestedEntity(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        this.entityTypeId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.items = response.data
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-nested' })
        })
    },

    fetchNestedEntityQuery () {
      this.startLoader({ id: 'tracker-nested' })
      const url = Vue.axios.agilisUrlQuery(
        this.applicationId,
        this.entityDatatable.querySelectMode)
      Vue.axios
        .post(
          url,
          { filterTerms: [{ field: 'trackerId', operator: 'EQUALS', value: this.trackerId }] },
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.items = response.data.items
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-nested' })
        })
    },

    async handleCreateNestedTracker () {
      this.dialog = true
      const { columns, formRows } = this.entityDatatable
      this.nestedTrackerDTO = Configuration.auth.getDTO(this.entityDatatable, this.selectedContext)
      const newItem = await this.openFormDialog(this.nestedTrackerDTO, columns, formRows)

      const url = Vue.axios.agilisUrlNestedEntity(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        this.entityTypeId)
      Vue.axios
        .post(
          url,
          newItem,
          Vue.axios.agilisRequestConfig(url))
        .then(() => {
          this.fetchData()
        })
        .catch((error) => {
          this.notifyError(error)
        })

      this.closeDialog()
    },

    async handleEditItem (item) {
      this.dialog = true
      const { columns, formRows } = this.entityDatatable
      this.nestedTrackerDTO = L.clone(Configuration.datatable.mapItemDTOForEdit(this.entityDatatable, item))
      const editItem = await this.openFormDialog(this.nestedTrackerDTO, columns, formRows)

      const url = Vue.axios.agilisUrlNestedEntityEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        this.entityTypeId,
        editItem.id)
      Vue.axios
        .put(
          url,
          editItem,
          Vue.axios.agilisRequestConfig(url))
        .then(() => {
          this.fetchData()
        })
        .catch((error) => {
          this.notifyError(error)
        })

      this.closeDialog()
    },

    handleDeleteItem (item) {
      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.deleteMessage(this.title)],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deleteEntity(item)
        }
      })
    },

    deleteEntity (item) {
      const url = Vue.axios.agilisUrlNestedEntityEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        this.entityTypeId,
        item.id)
      Vue.axios
        .delete(
          url,
          {
            ...Vue.axios.agilisRequestConfig(url),
            data: item
          })
        .then(() => {
          this.fetchData()
        })
        .catch((error) => {
          this.notifyError(error)
        })
    },

    closeDialog () {
      this.nestedTrackerDTO = {}
      this.dialog = false
    },

    renderColumn (colId, item) {
      const colConfig = this.entityDatatable.columns[colId]
      if (colConfig) {
        return Configuration.datatable.renderColumn(colConfig, item)
      }
    },

    entityUrl (entity) {
      return entity ? this.$router.agilis.urlViewTracker({
        trackerId: entity.id,
        trackerTypeId: this.entityTypeId
      }) : ''
    },

    handleEntityClick (entity) {
      this.$router.agilis.viewTracker({
        trackerId: entity.id,
        trackerTypeId: this.entityTypeId
      })
    },

    userCanDoAndAccessFeature (operation) {
      return Configuration.auth.userCanDoAndAccessFeature(
        this.selectedContext, this.entityTypeId, operation, this.trackerDTO, this.userId)
    }
  }
}
